import {
  TRESPONSE_LL_TASK_MANAGER,
  TRESPONSE_LINE_STRIP_PLANNED_NUMBERS,
  TRESPONSE_PLANNED_ORDER_PROGRESS,
  TMaterialInHouseByOtlResponse,
  TTnaStepMaterialDetails,
} from '@monorepo/types';
import { ModifiersZTableModel } from '../../table.modifier';
import { THeaderCells, TTableCellTree } from '../../table.type';
import { DateUtils } from '@monorepo/utils';
import moment from 'moment';

const TOTAL_TEXT = 'Total';

function getTaskStatusBgColor(taskStatus: string) {
  switch (taskStatus) {
    case 'PENDING':
      return 'grey';
    case 'IN_PROGRESS':
      return 'yellow';
    case 'COMPLETED':
      return 'green';
    case 'FAILED_WITH_RETRY':
      return 'red';
    default:
      return 'grey';
  }
}
function getPlannedOrderProgressTableItemsWithTotal(
  items: TRESPONSE_PLANNED_ORDER_PROGRESS['lineWisePlannedAndActualValues'],
): TRESPONSE_PLANNED_ORDER_PROGRESS['lineWisePlannedAndActualValues'] {
  if (items.length === 0) return [];

  const { totalPlannedQty, totalActualQty, totalVariance } = items.reduce(
    (sum, item) => {
      return {
        totalPlannedQty: sum.totalPlannedQty + item.plannedQty,
        totalActualQty: sum.totalActualQty + item.actualQty,
        totalVariance: sum.totalVariance + item.variance,
      };
    },
    { totalPlannedQty: 0, totalActualQty: 0, totalVariance: 0 },
  );

  return [
    {
      date: TOTAL_TEXT,
      line: '- -',
      actualQty: totalActualQty,
      plannedQty: totalPlannedQty,
      variance: totalVariance,
    },
    ...items,
  ];
}
export const PLANNED_ORDER_PROGRESS_TABLE_MODEL = {
  createModel: (items: TRESPONSE_PLANNED_ORDER_PROGRESS['lineWisePlannedAndActualValues']) => {
    return ModifiersZTableModel.createTableModel(
      getPlannedOrderProgressTableItemsWithTotal(items),
      {
        headerConfig: [
          {
            type: 'TEXT_CELL',
            text: 'DATE',
          },
          {
            type: 'TEXT_CELL',
            text: 'LINE',
          },
          {
            type: 'TEXT_CELL',
            text: 'PLANNED QUANTITY',
          },
          {
            type: 'TEXT_CELL',
            text: 'ACTUAL QUANTITY',
          },
          {
            type: 'TEXT_CELL',
            text: 'VARIANCE',
          },
        ],
      },
      (item) => {
        return {
          type: 'ROW',
          rowItem: item,
          cells: [
            {
              type: 'TEXT_CELL',
              text: item.date === TOTAL_TEXT ? TOTAL_TEXT : DateUtils.getDisplayDateStringFromDate(item.date),
            },
            {
              type: 'TEXT_CELL',
              text: item.line,
            },
            {
              type: 'TEXT_CELL',
              text: item.plannedQty,
            },
            {
              type: 'TEXT_CELL',
              text: item.actualQty,
            },
            {
              type: 'TEXT_CELL',
              text: item.variance,
            },
          ],
        };
      },
    );
  },
};

export const LL_TASK_MANAGER_TABLE_MODEL = {
  createModel: (items: TRESPONSE_LL_TASK_MANAGER['tasks']) => {
    return ModifiersZTableModel.createTableModel(
      items,
      {
        padding: 8,
        headerConfig: [
          {
            type: 'TEXT_CELL',
            text: 'TASK NAME',
          },
          {
            type: 'TEXT_CELL',
            text: 'ACTIONS',
          },
          {
            type: 'TEXT_CELL',
            text: 'EVENT STARTED',
          },
          {
            type: 'TEXT_CELL',
            text: 'EVENT COMPLETED',
          },
          {
            type: 'TEXT_CELL',
            text: 'EVENT STATUS',
          },
        ],
      },
      (item) => {
        return {
          type: 'ROW',
          rowItem: item,
          cells: [
            {
              type: 'TEXT_CELL',
              text: item.taskName,
            },
            {
              type: 'TEXT_LIST_CELL',
              text: item.actions,
            },
            {
              type: 'TEXT_CELL',
              text: item.taskCreatedAt,
            },
            {
              type: 'TEXT_CELL',
              text: item.taskCompletedAt || '--',
            },
            {
              type: 'TEXT_CELL',
              text: '',
              tag: {
                tagContent: item.taskStatus,
                tagBgColor: getTaskStatusBgColor(item.taskStatus),
              },
            },
          ],
        };
      },
    );
  },
};

export const STRIP_PLANNED_NUMBERS_TABLE_MODEL = {
  createModel: (PlannedQtyByDate: TRESPONSE_LINE_STRIP_PLANNED_NUMBERS) => {
    const rows: { label: string; key: keyof TRESPONSE_LINE_STRIP_PLANNED_NUMBERS[number] }[] = [
      { label: 'Planned Qty', key: 'qty' },
      { label: 'Planning Efficiency', key: 'effectiveEfficiency' },
      { label: 'No of Operators', key: 'operatorCount' },
    ];

    return ModifiersZTableModel.createTableModel(
      rows,
      {
        headerConfig: getPlannedDatesHeaderRow(PlannedQtyByDate),
      },
      (item) => {
        return {
          type: 'ROW',
          rowItem: item,
          cells: getPlannedQtyRows(PlannedQtyByDate, item),
        };
      },
    );
  },
};

const getPlannedDatesHeaderRow = (headerInfo: TRESPONSE_LINE_STRIP_PLANNED_NUMBERS) => {
  return [
    {
      type: 'TEXT_CELL' as const,
      text: 'Date',
      stickyType: 'LEFT' as const,
      stickyPosition: 1,
    },
    ...headerInfo.map((hInfo) => ({
      type: 'TEXT_LIST_CELL' as const,
      text: [moment(hInfo.date).format('MMM YYYY'), moment(hInfo.date).format('D')],
    })),
  ];
};

const getPlannedQtyRows = (
  itemData: TRESPONSE_LINE_STRIP_PLANNED_NUMBERS,
  row: { label: string; key: keyof TRESPONSE_LINE_STRIP_PLANNED_NUMBERS[number] },
) => {
  return [
    {
      type: 'TEXT_CELL' as const,
      text: row.label,
      align: 'left' as const,
    },
    ...itemData.map((itemInfo) => ({
      type: 'TEXT_CELL' as const,
      text:
        row.key === 'effectiveEfficiency' && itemInfo[row.key] ? `${itemInfo[row.key]}%` : itemInfo[row.key] || '--',
    })),
  ];
};

export const MATERIAL_IN_HOUSE_TABLE_MODEL = {
  createModel: (
    headerInfo: TMaterialInHouseByOtlResponse['articleHeaderInfo'],
    data: TTnaStepMaterialDetails['articles'],
    showSuppliersColumn: boolean,
  ) => {
    return ModifiersZTableModel.createTableModel(
      data,
      {
        headerConfig: headerInfo
          .map(
            (item): THeaderCells<unknown> => ({
              type: 'TEXT_CELL',
              text: item.displayName,
              maxWidthInPx: 150,
            }),
          )
          .concat(showSuppliersColumn ? [{ type: 'TEXT_CELL', text: 'Suppliers' }] : []),
      },
      (item) => {
        return {
          type: 'ROW',
          rowItem: item,
          cells: headerInfo
            .map<TTableCellTree>((x) => ({
              type: 'TEXT_CELL',
              text: item.articleInfo[x.key] ?? '- -',
            }))
            .concat(
              showSuppliersColumn
                ? [
                    item.supplierInfo?.length
                      ? {
                          type: 'TEXT_CELL',
                          text: 'View Suppliers',
                          hrefActionCB: 'VIEW_SUPPLIERS_DETAILS_BY_ARTICLE',
                        }
                      : { type: 'TEXT_CELL', text: 'Not Applicable' },
                  ]
                : [],
            ),
        };
      },
    );
  },
};

export const MATERIAL_IN_HOUSE_SUPPLIERS_INFO_TABLE_MODEL = {
  createModel: (
    headerInfo: NonNullable<TMaterialInHouseByOtlResponse['supplierHeaderInfo']>,
    data: NonNullable<TTnaStepMaterialDetails['articles'][number]['supplierInfo']>,
  ) => {
    return ModifiersZTableModel.createTableModel(
      data,
      {
        headerConfig: headerInfo.map(
          (item): THeaderCells<unknown> => ({
            type: 'TEXT_CELL',
            text: item.displayName,
            maxWidthInPx: 150,
          }),
        ),
      },
      (item) => {
        return {
          type: 'ROW',
          rowItem: item,
          cells: headerInfo.map<TTableCellTree>((x) => ({
            type: 'TEXT_CELL',
            text: item[x.key] ?? '- -',
          })),
        };
      },
    );
  },
};
